// Single source of truth for how Search.js's filter state round-trips through
// the URL querystring. Each entry declares its querystring key, its
// corresponding state field, the default value for that field, and how to
// convert between the two. A param is only written to the URL when its state
// value differs from the default, so an all-default search collapses to a
// bare "/search".

export const orderingOptions = [
	{
		label: "Last Modified Date (Newest First)",
		value: "modifiedDateDescending",
		id: 0,
	},
	{
		label: "Last Modified Date (Oldest First)",
		value: "modifiedDateAscending",
		id: 1,
	},
	{
		label: "Relevance",
		value: "relevance",
		id: 2,
	},
];

export const docSearchModeOptions = [
	{
		label: "File Name and Content",
		value: "docs",
		id: 0,
	},
	{
		label: "File Name Only",
		value: "docFileName",
		id: 1,
	},
];

// recordTypeFilter is legacy/unused by submit() and the UI today, but the
// "type" querystring key has existed for a while, so it's kept round-tripping
// for anyone with an old bookmarked/shared link.
export const recordTypeOptions = [
	{
		label: "Documents (File Name and Content)",
		value: "docs",
		id: 0,
	},
	{
		label: "Documents (File Name)",
		value: "docFileName",
		id: 3,
	},
	{
		label: "Emails",
		value: "email",
		id: 1,
	},
	{
		label: "Matter Notes",
		value: "matterNote",
		id: 2,
	},
];

const listIsEmpty = (list) => !list || list.length === 0;

// Matter IDs can be negative (-1 for "Public Firm Docs", 0 for "Private Firm
// Docs"), everything else is a non-negative row ID.
const signedIdListToState = (raw) =>
	raw
		.split(":")
		.filter((id) => /^-?\d+$/.test(id))
		.map((id) => ({ id: parseInt(id, 10) }));

const unsignedIdListToState = (raw) =>
	raw
		.split(":")
		.filter((id) => /^\d+$/.test(id))
		.map((id) => ({ id: parseInt(id, 10) }));

const idListToParam = (list) =>
	listIsEmpty(list) ? "" : list.map((item) => item.id).join(":");

// fileExtensionFilterOptions (backend: fileExtensionCategories.js) has no
// numeric id, only a string `value` (e.g. "Excel", "PDF"), so this filter is
// keyed differently from the others.
const valueListToState = (raw) =>
	raw
		.split(":")
		.filter(Boolean)
		.map((value) => ({ value }));

const valueListToParam = (list) =>
	listIsEmpty(list) ? "" : list.map((item) => item.value).join(":");

const findOptionById = (options, id) =>
	options.find((option) => option.id === id);

export const searchUrlParamSchema = [
	{
		key: "q",
		stateKey: "query",
		default: "",
		deserialize: (raw) => raw,
		serialize: (value) => value || "",
		isDefault: (value) => !value,
	},
	{
		key: "m",
		stateKey: "matterFilter",
		default: [],
		deserialize: signedIdListToState,
		serialize: idListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "docSearchMode",
		stateKey: "docSearchMode",
		default: docSearchModeOptions[0],
		deserialize: (raw) =>
			findOptionById(docSearchModeOptions, parseInt(raw, 10)) ||
			docSearchModeOptions[0],
		serialize: (value) => String((value || docSearchModeOptions[0]).id),
		isDefault: (value) => !value || value.id === docSearchModeOptions[0].id,
	},
	{
		key: "from",
		stateKey: "fromDate",
		default: null,
		deserialize: (raw) => (raw ? new Date(parseInt(raw, 10)) : null),
		serialize: (value) => (value ? String(value.getTime()) : ""),
		isDefault: (value) => !value,
	},
	{
		key: "to",
		stateKey: "toDate",
		default: null,
		deserialize: (raw) => (raw ? new Date(parseInt(raw, 10)) : null),
		serialize: (value) => (value ? String(value.getTime()) : ""),
		isDefault: (value) => !value,
	},
	{
		key: "docType",
		stateKey: "documentTypeFilter",
		default: [],
		deserialize: unsignedIdListToState,
		serialize: idListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "docStatus",
		stateKey: "documentStatusFilter",
		default: [],
		deserialize: unsignedIdListToState,
		serialize: idListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "docTags",
		stateKey: "documentTagsFilter",
		default: [],
		deserialize: unsignedIdListToState,
		serialize: idListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "fileType",
		stateKey: "fileExtensionFilter",
		default: [],
		deserialize: valueListToState,
		serialize: valueListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "users",
		stateKey: "usersFilter",
		default: [],
		deserialize: unsignedIdListToState,
		serialize: idListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "lock",
		stateKey: "usersLockFilter",
		default: [],
		deserialize: unsignedIdListToState,
		serialize: idListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "type",
		stateKey: "recordTypeFilter",
		default: [],
		deserialize: (raw) =>
			unsignedIdListToState(raw).filter((item) =>
				recordTypeOptions.some((option) => option.id === item.id)
			),
		serialize: idListToParam,
		isDefault: listIsEmpty,
	},
	{
		key: "fav",
		stateKey: "onlySearchMyFavorites",
		default: false,
		deserialize: (raw) => parseInt(raw, 10) === 1,
		serialize: (value) => (value ? "1" : "0"),
		isDefault: (value) => !value,
	},
	{
		key: "incDel",
		stateKey: "includeDeletedItemsInSearch",
		default: false,
		deserialize: (raw) => parseInt(raw, 10) === 1,
		serialize: (value) => (value ? "1" : "0"),
		isDefault: (value) => !value,
	},
	{
		key: "order",
		stateKey: "ordering",
		default: orderingOptions[0],
		deserialize: (raw) =>
			findOptionById(orderingOptions, parseInt(raw, 10)) ||
			orderingOptions[0],
		serialize: (value) => String((value || orderingOptions[0]).id),
		isDefault: (value) => !value || value.id === orderingOptions[0].id,
	},
	{
		key: "tab",
		stateKey: "activeTab",
		default: 0,
		deserialize: (raw) => parseInt(raw, 10) || 0,
		serialize: (value) => String(value || 0),
		isDefault: (value) => !value,
	},
];

export const searchStateDefaults = () => {
	var defaults = {};
	searchUrlParamSchema.forEach((param) => {
		defaults[param.stateKey] = param.default;
	});
	return defaults;
};

// queryStringValues is the object produced by query-string's parse().
// Only keys that are present and non-empty override the caller's defaults,
// so a missing/blank param falls back to whatever the caller already has.
export const parseSearchUrlParams = (queryStringValues) => {
	var state = {};
	searchUrlParamSchema.forEach((param) => {
		const raw = queryStringValues[param.key];
		if (raw !== undefined && raw !== null && raw !== "") {
			state[param.stateKey] = param.deserialize(raw);
		}
	});
	return state;
};

// Returns a { [querystringKey]: string } map containing only the params
// whose value differs from its default.
export const serializeSearchUrlParams = (state) => {
	var params = {};
	searchUrlParamSchema.forEach((param) => {
		const value = state[param.stateKey];
		if (!param.isDefault(value)) {
			params[param.key] = param.serialize(value);
		}
	});
	return params;
};
